import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Stack from '@mui/joy/Stack';
import JoyInput from '@mui/joy/Input';
import JoyButton from '@mui/joy/Button';

import Card from '../../components/UI/Card/Card';
import ErrorModal from '../../components/UI/ErrorModal/ErrorModal';
import {
  useVerifyTwoFactorChallengeMutation,
  useSendTwoFactorEmailCodeMutation,
  useSendTwoFactorSetupEmailCodeMutation,
  useVerifyTwoFactorSetupEmailCodeMutation,
  TwoFactorMethod
} from '../../services/api';
import { isFetchBaseQueryError } from '../../utils/isFetchBaseQueryError';
import Athenalogo from '../../assets/images/Athena_logo_final.png';
import '../Login/Login.css';

interface LocationState {
  challengeToken?: string;
  hasTotp?: boolean;
  email?: string;
  urlBeforeRedirect?: string;
  preselectedMethod?: TwoFactorMethod;
}

type View = 'picker' | 'code';

type CodeMode = TwoFactorMethod | 'setup';

const ChallengeTwoFactor = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { challengeToken, hasTotp = false, email, urlBeforeRedirect, preselectedMethod } =
    (state as LocationState | null) ?? {};

  const [view, setView] = useState<View>(preselectedMethod ? 'code' : 'picker');
  const [codeMode, setCodeMode] = useState<CodeMode>(preselectedMethod ?? 'totp');
  const [code, setCode] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [verifyChallenge, { isLoading: isVerifying, error: verifyError, reset: resetVerify }] =
    useVerifyTwoFactorChallengeMutation();
  const [sendEmailCode, { isLoading: isSendingEmailCode, error: sendError, reset: resetSend }] =
    useSendTwoFactorEmailCodeMutation();
  const [sendSetupEmailCode, { isLoading: isSendingSetupCode, error: sendSetupError, reset: resetSendSetup }] =
    useSendTwoFactorSetupEmailCodeMutation();
  const [verifySetupEmailCode, { isLoading: isVerifyingSetupCode, error: verifySetupError, reset: resetVerifySetup }] =
    useVerifyTwoFactorSetupEmailCodeMutation();
  const isSending = isSendingEmailCode || isSendingSetupCode;

  const clearErrors = () => {
    resetVerify();
    resetSend();
    resetSendSetup();
    resetVerifySetup();
  };

  const isExpiredTokenError = (err: unknown): boolean => {
    const e = err as { status?: number; data?: { message?: string } } | undefined;
    if (e?.status !== 401) return false;
    const msg = e?.data?.message?.toLowerCase() ?? '';
    return msg.includes('challenge token');
  };

  useEffect(() => {
    if (!challengeToken) {
      navigate('/login', { replace: true });
    }
  }, [challengeToken, navigate]);

  const initialSendRef = useRef(false);
  useEffect(() => {
    if (preselectedMethod !== 'email' || !challengeToken || initialSendRef.current) return;
    initialSendRef.current = true;
    sendEmailCode({ challengeToken })
      .unwrap()
      .catch((err) => {
        if (isExpiredTokenError(err)) {
          setSessionExpired(true);
          return;
        }
        console.log('[ChallengeTwoFactor initial send]', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedMethod, challengeToken]);

  const submitHandler = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (!challengeToken || !/^\d{6}$/.test(code)) return;

    try {
      if (codeMode === 'setup') {
        const { setupToken } = await verifySetupEmailCode({ challengeToken, code }).unwrap();
        navigate('/2fa/setup', {
          state: { setupToken, challengeToken, email, urlBeforeRedirect }
        });
        return;
      }

      await verifyChallenge({ challengeToken, code, method: codeMode }).unwrap();
      navigate(urlBeforeRedirect && urlBeforeRedirect !== '/login' ? urlBeforeRedirect : '/');
    } catch (err) {
      if (isExpiredTokenError(err)) {
        setSessionExpired(true);
        return;
      }
      console.log('[ChallengeTwoFactor]', err);
    }
  };

  const handleResend = async () => {
    if (!challengeToken) return;
    try {
      if (codeMode === 'setup') {
        await sendSetupEmailCode({ challengeToken }).unwrap();
      } else {
        await sendEmailCode({ challengeToken }).unwrap();
      }
    } catch (err) {
      if (isExpiredTokenError(err)) {
        setSessionExpired(true);
        return;
      }
      console.log('[ChallengeTwoFactor resend]', err);
    }
  };

  const handlePickEmail = async () => {
    if (!challengeToken) return;
    try {
      await sendEmailCode({ challengeToken }).unwrap();
      setCodeMode('email');
      setCode('');
      setView('code');
    } catch (err) {
      if (isExpiredTokenError(err)) {
        setSessionExpired(true);
        return;
      }
      console.log('[ChallengeTwoFactor pick email]', err);
    }
  };

  const handlePickAuthenticator = async () => {
    if (hasTotp) {
      setCodeMode('totp');
      setCode('');
      setView('code');
      return;
    }
    if (!challengeToken) return;
    try {
      await sendSetupEmailCode({ challengeToken }).unwrap();
      setCodeMode('setup');
      setCode('');
      setView('code');
    } catch (err) {
      if (isExpiredTokenError(err)) {
        setSessionExpired(true);
        return;
      }
      console.log('[ChallengeTwoFactor pick authenticator]', err);
    }
  };

  const error = verifyError || sendError || sendSetupError || verifySetupError;
  const errorMessage =
    (isFetchBaseQueryError(error) ? (error.data as { message?: string } | undefined)?.message : '') ?? '';

  if (sessionExpired) {
    return (
      <div className="auth-container">
        <Card className="authentication">
          <img src={Athenalogo} alt="logo" />
          <p className="headline">Session expired</p>
          <div style={{ padding: '0 5%' }}>
            <p style={{ fontSize: '0.95rem', color: '#363636' }}>
              Your verification session has expired. Please log in again to continue.
            </p>
          </div>
          <Stack spacing={2} sx={{ padding: '0 5%', marginTop: '1rem' }}>
            <JoyButton onClick={() => navigate('/login', { replace: true })} fullWidth>
              Back to login
            </JoyButton>
          </Stack>
        </Card>
      </div>
    );
  }

  if (view === 'picker') {
    return (
      <div className="auth-container">
        <ErrorModal error={errorMessage} header="Two-Factor Verification Error" onClear={clearErrors} />
        <Card className="authentication">
          <img src={Athenalogo} alt="logo" />
          <p className="headline">Two-factor verification</p>
          <div style={{ padding: '0 5%' }}>
            <p style={{ fontSize: '0.95rem', color: '#363636' }}>
              {hasTotp
                ? 'Open your authenticator app to get a 6-digit code.'
                : 'Set up an authenticator app for a faster, more secure sign-in.'}
            </p>
          </div>
          <Stack spacing={2} sx={{ padding: '0 5%', marginTop: '1rem' }}>
            <JoyButton
              onClick={handlePickAuthenticator}
              disabled={isSending}
              loading={isSendingSetupCode}
              fullWidth
            >
              {hasTotp ? 'Use authenticator' : 'Set up authenticator'}
            </JoyButton>
            <JoyButton
              variant="plain"
              color="neutral"
              onClick={handlePickEmail}
              disabled={isSending}
              loading={isSendingEmailCode}
              fullWidth
            >
              Use email code instead
            </JoyButton>
            <JoyButton variant="plain" color="neutral" onClick={() => navigate('/login')} fullWidth>
              Back to login
            </JoyButton>
          </Stack>
        </Card>
      </div>
    );
  }

  const emailedCodeIntro = email ? (
    <>
      We sent a 6-digit code to <strong>{email}</strong>.
    </>
  ) : (
    'We sent a 6-digit code to your email.'
  );

  const description =
    codeMode === 'setup' ? (
      <>Before setting up an authenticator, confirm it’s you. {emailedCodeIntro} Enter it below to continue.</>
    ) : codeMode === 'email' ? (
      <>{emailedCodeIntro} Enter it below to finish signing in.</>
    ) : (
      'Open your authenticator app and enter the 6-digit code to finish signing in.'
    );

  return (
    <div className="auth-container">
      <ErrorModal error={errorMessage} header="Two-Factor Verification Error" onClear={clearErrors} />
      <Card className="authentication">
        <img src={Athenalogo} alt="logo" />
        <p className="headline">Two-factor verification</p>
        <div style={{ padding: '0 5%' }}>
          <p style={{ fontSize: '0.95rem', color: '#363636' }}>{description}</p>
        </div>
        <form onSubmit={submitHandler}>
          <Stack spacing={2} sx={{ padding: '0 5%', marginTop: '1rem' }}>
            <JoyInput
              id="code"
              type="text"
              slotProps={{
                input: { inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6, pattern: '\\d*' }
              }}
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              sx={{ fontSize: '1.1rem', textAlign: 'center' }}
            />
            <JoyButton
              type="submit"
              disabled={code.length !== 6 || isVerifying || isVerifyingSetupCode}
              loading={isVerifying || isVerifyingSetupCode}
              fullWidth
            >
              Verify
            </JoyButton>
            {codeMode !== 'totp' && (
              <JoyButton
                variant="plain"
                color="neutral"
                onClick={handleResend}
                disabled={isSending}
                loading={isSending}
                fullWidth
              >
                Resend code
              </JoyButton>
            )}
            {codeMode !== 'email' && (
              <JoyButton
                variant="plain"
                color="neutral"
                onClick={handlePickEmail}
                disabled={isSending}
                loading={isSendingEmailCode}
                fullWidth
              >
                Use email code instead
              </JoyButton>
            )}
            <JoyButton variant="plain" color="neutral" onClick={() => navigate('/login')} fullWidth>
              Back to login
            </JoyButton>
          </Stack>
        </form>
      </Card>
    </div>
  );
};

export default ChallengeTwoFactor;
